window.BlogsPage = function BlogsPage() {
  const [blogsList, setBlogsList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeArticle, setActiveArticle] = React.useState(null);

  React.useEffect(() => {
    fetchBlogs();
    const handleSync = () => fetchBlogs();
    window.addEventListener('novatek_blogs_updated', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('novatek_blogs_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await window.apiClient.get('blogs.php');
      if (res && res.success && Array.isArray(res.data)) {
        setBlogsList(res.data);
      }
    } catch (e) {
      console.warn('Blogs fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.SEOHelper) {
      window.SEOHelper.updatePageMeta(
        "Technical Insights & Engineering Articles | Novatek Custom Test Systems",
        "Explore authoritative technical articles, test rig design guides, railway brake testing standards, and industrial automation whitepapers."
      );
    }
  }, []);

  // Compute dynamic category list from actual blogs
  const categories = React.useMemo(() => {
    const set = new Set();
    set.add('All');
    blogsList.forEach(b => {
      if (b.category) set.add(b.category);
    });
    return Array.from(set);
  }, [blogsList]);

  const filteredBlogs = blogsList.filter(b => {
    const matchesCat = selectedCategory === 'All' || b.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      (b.title && b.title.toLowerCase().includes(q)) || 
      (b.summary && b.summary.toLowerCase().includes(q)) || 
      (b.author && b.author.toLowerCase().includes(q)) ||
      (b.category && b.category.toLowerCase().includes(q));
    return matchesCat && matchesSearch;
  });

  return (
    <div className="bg-slate-50 min-h-screen py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 sm:space-y-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="font-sans text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase block">
            Technical Knowledge Hub
          </span>
          <h1 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Engineering Insights & Technical Guides
          </h1>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-sm sm:text-base">
            Practical engineering guides, validation standards, sensor selection methodologies, and automation architecture insights from Novatek's design bureau.
          </p>
        </div>

        {/* Redesigned Clean Search & Category Filter Bar */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
            
            {/* Category Segmented Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 custom-scrollbar flex-1">
              {categories.map((cat) => {
                const count = cat === 'All' 
                  ? blogsList.length 
                  : blogsList.filter(b => b.category === cat).length;
                const isSelected = selectedCategory === cat;

                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                      isSelected
                        ? 'bg-secondary text-white shadow-md shadow-orange-500/20'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80'
                    }`}
                  >
                    <span>{cat}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-sans font-bold ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-slate-200/70 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Integrated Search Input */}
            <div className="relative w-full lg:w-80 shrink-0">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base">
                search
              </span>
              <input
                type="text"
                placeholder="Search articles, guides, authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-sans outline-none focus:border-secondary focus:bg-white transition-all shadow-2xs"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs p-1"
                >
                  ✕
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Blog Article Cards Grid */}
        {loading ? (
          <div className="p-16 text-center text-slate-400 font-sans text-xs flex items-center justify-center gap-2">
            <span className="material-symbols-outlined animate-spin text-secondary">sync</span>
            <span>Loading latest engineering articles...</span>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="p-16 text-center bg-white rounded-3xl border border-slate-200 space-y-3">
            <span className="material-symbols-outlined text-4xl text-slate-300">article</span>
            <h3 className="font-sans text-base font-bold text-slate-800">No Articles Found</h3>
            <p className="font-sans text-xs text-slate-500">Try adjusting your category filter or search keywords.</p>
            <button
              onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
              className="bg-slate-900 text-white font-sans text-xs font-bold px-4 py-2 rounded-xl"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredBlogs.map((article) => (
              <div
                key={article.id || article.slug}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="h-48 bg-slate-900 overflow-hidden relative">
                    <img
                      src={window.resolveMediaUrl(article.image || article.cover_image, 'machine')}
                      alt={article.title}
                      onError={(e) => window.handleImageError(e, 'machine')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-slate-900/85 text-secondary text-[11px] font-sans font-bold px-2.5 py-0.5 rounded border border-orange-500/30">
                      {article.category}
                    </span>
                  </div>

                  <div className="p-6 space-y-3">
                    <div className="flex items-center gap-3 text-xs font-sans text-slate-400">
                      <span>{article.date || article.publish_date}</span>
                      <span>•</span>
                      <span>{article.readTime || article.read_time || '5 min read'}</span>
                    </div>

                    <h3 className="font-sans text-base sm:text-lg font-bold text-slate-900 group-hover:text-secondary transition-colors leading-snug line-clamp-2">
                      {article.title}
                    </h3>

                    <p className="font-sans text-xs text-slate-600 leading-relaxed line-clamp-3">
                      {article.summary}
                    </p>
                  </div>
                </div>

                <div className="p-6 pt-0 border-t border-slate-100 bg-slate-50/50">
                  <button
                    onClick={() => setActiveArticle(article)}
                    className="w-full mt-4 bg-slate-900 group-hover:bg-secondary text-white py-2.5 rounded-xl font-sans text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow"
                  >
                    <span>Read Technical Guide</span>
                    <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}

      </div>

      {/* Article Detail View Modal */}
      {activeArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 shadow-2xl p-6 sm:p-10 space-y-6 relative custom-scrollbar">
            
            <button
              onClick={() => setActiveArticle(null)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 p-2 rounded-full transition-colors cursor-pointer"
            >
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>

            <div className="space-y-3 pr-10">
              <span className="font-sans text-xs font-bold text-secondary bg-orange-50 px-3 py-1 rounded inline-block">
                {activeArticle.category}
              </span>
              <h2 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                {activeArticle.title}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-xs font-sans text-slate-400 border-b border-slate-100 pb-4">
                <span>By <strong className="text-slate-700">{activeArticle.author || 'Novatek Design Bureau'}</strong></span>
                <span>•</span>
                <span>{activeArticle.date || activeArticle.publish_date}</span>
                <span>•</span>
                <span>{activeArticle.readTime || activeArticle.read_time || '5 min read'}</span>
              </div>
            </div>

            {/* Featured Image inside Article */}
            {(activeArticle.image || activeArticle.cover_image) && (
              <div className="h-64 sm:h-80 w-full rounded-2xl overflow-hidden bg-slate-900 border border-slate-200">
                <img
                  src={window.resolveMediaUrl(activeArticle.image || activeArticle.cover_image, 'machine')}
                  alt={activeArticle.title}
                  onError={(e) => window.handleImageError(e, 'machine')}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Article Content Rendered */}
            <div 
              className="prose prose-slate max-w-none text-sm font-sans text-slate-700 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: activeArticle.content }}
            />

            <div className="pt-6 border-t border-slate-200 flex justify-between items-center">
              <button
                onClick={() => setActiveArticle(null)}
                className="text-xs font-sans font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">arrow_back</span>
                <span>Back to Articles</span>
              </button>
              <button
                onClick={() => window.print()}
                className="bg-slate-900 hover:bg-secondary text-white text-xs font-sans font-bold px-5 py-2.5 rounded-xl flex items-center gap-1.5 cursor-pointer shadow transition-colors"
              >
                <span className="material-symbols-outlined text-sm">print</span>
                <span>Print / Save PDF</span>
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
